// src/pages/HomePage.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CoverSlider from "../components/CoverSlider";
import VideosSection from "../components/VideosSection";
import MerchSection from "../components/MerchSection";
import TripleCtas from "../components/TripleCtas";
import MediaRenderer from "../components/MediaRenderer";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { isPublicPageListed, mergeSiteContent } from "../utils/helpers";
import { typographyFor } from "../lib/typography";

export default function HomePage() {
  const [ymsImages, setYmsImages] = useState([]);
  const [site, setSite] = useState(() => mergeSiteContent());
  const [currentImage, setCurrentImage] = useState(0);

  // 🔥 Récup images YMS depuis Firestore
  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setSite(mergeSiteContent(data));
          if (data.ymsImages) setYmsImages(data.ymsImages.filter(Boolean));
        } else {
          setSite(mergeSiteContent());
        }
      },
      (err) => console.error("Erreur Firestore YMS:", err)
    );
    return () => off();
  }, []);

  // 🔥 Slider auto (8 sec)
  useEffect(() => {
    if (ymsImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % ymsImages.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [ymsImages]);

  const { texts, pages, visibility } = site;
  const newsHero = pages.home.newsHero || {};
  const showPublicPage = (key) => isPublicPageListed(site, key);

  return (
    <div className="bg-black text-white font-sans min-h-screen flex flex-col overflow-x-hidden">
      {/* ===== NEWS HERO ===== */}
      {visibility.homeNewsHero && showPublicPage("news") && newsHero.enabled !== false && (
        <section className="relative min-h-[72vh] overflow-hidden bg-black px-6 py-20 md:min-h-[82vh] md:px-20">
          <MediaRenderer
            src={newsHero.video}
            type={newsHero.mediaType || "video"}
            fallback={newsHero.fallbackImage}
            alt=""
            background
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/40" />
          <div className="relative mx-auto flex min-h-[56vh] max-w-7xl flex-col justify-end md:min-h-[66vh]">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.25em] text-red-500">
              News
            </p>
            <h1 className="max-w-5xl text-4xl font-black uppercase leading-none md:text-7xl" style={typographyFor(site, "homeNewsTitle")}>
              {newsHero.title}
            </h1>
            {newsHero.subtitle && (
              <p className="readable-copy mt-5 max-w-2xl text-base text-gray-200 md:text-lg" style={typographyFor(site, "homeNewsText")}>
                {newsHero.subtitle}
              </p>
            )}
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={newsHero.watchUrl || "https://youtube.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-white px-6 py-3 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white"
              >
                {newsHero.watchLabel || "Regarder"}
              </a>
              {showPublicPage("news") && (
                <Link
                  to={newsHero.discoverUrl || "/news"}
                  className="rounded-full border border-white/30 px-6 py-3 text-sm font-bold uppercase text-white transition hover:border-white hover:bg-white hover:text-black"
                >
                  {newsHero.discoverLabel || "Découvrir"}
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ===== SECTION YMS ===== */}
      {visibility.homeYms && showPublicPage("artistes") && (
      <section className="relative bg-black text-white overflow-hidden min-h-screen flex items-center justify-between px-8 py-20 md:px-24">
        <div className="z-10 max-w-xl">
          <span className="inline-block bg-white text-black text-xs font-semibold px-2 py-1 mb-4 uppercase tracking-wider rounded">
            {pages.home.ymsBadge}
          </span>
          <h2 className="text-4xl md:text-6xl font-extrabold leading-tight mb-4" style={typographyFor(site, "homeYmsTitle")}>
            {texts.ymsTitle || "YMS devoile son univers unique"}
          </h2>
          <p className="readable-copy text-lg text-gray-300 mb-6" style={typographyFor(site, "homeYmsText")}>
            {texts.ymsDescription ||
              "C'est le 15 avril 2004 a Villeneuve-Saint-Georges que tout commence: YMS voit le jour. Il plonge dans la musique des 2014, comme beatmaker et inge son, forme par son pere Aumar Pulhosow, artiste et ex-membre du groupe Djolof signe chez Universal."}
          </p>
          {showPublicPage("artistes") && (
            <Link
              to={pages.home.ymsButtonUrl || "/artistes"}
              className="border-2 border-white px-6 py-2 rounded-full text-white font-bold hover:bg-white hover:text-black transition"
              aria-label="Découvrir l'univers de YMS"
            >
              {texts.ymsButton || "Decouvrir"}
            </Link>
          )}
        </div>

        {/* DIAPORAMA YMS */}
        <div className="hidden md:flex flex-col items-center w-1/2 relative z-0">
          <div className="w-full h-[800px] relative flex items-center justify-center bg-black rounded-lg overflow-hidden">
            {ymsImages.map((img, index) => (
              <MediaRenderer
                key={index}
                src={img}
                type="auto"
                alt={`YMS ${index}`}
                background
                className={`absolute inset-0 mx-auto max-h-full max-w-full object-contain rounded-2xl transition-opacity duration-1000 ease-in-out ${
                  index === currentImage ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  maskImage:
                    "linear-gradient(to left, rgba(0,0,0,1) 85%, rgba(0,0,0,0))",
                  WebkitMaskImage:
                    "linear-gradient(to left, rgba(0,0,0,1) 85%, rgba(0,0,0,0))",
                }}
              />
            ))}
          </div>

          {/* Indicateurs */}
          <div className="flex justify-center mt-4 gap-2">
            {ymsImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentImage === index
                    ? "bg-white w-6"
                    : "bg-gray-500 hover:bg-gray-400"
                }`}
                aria-label={`Aller à l'image ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ===== HERO UNLIMITED SESSION ===== */}
      {visibility.homeSession && showPublicPage("studio") && (
      <section className="bg-[#121212] text-white px-8 md:px-24 py-24 flex flex-col md:flex-row items-center justify-between relative">
        <div className="max-w-2xl">
          <h1 className="text-5xl md:text-7xl font-extrabold uppercase mb-6 leading-tight tracking-tight notranslate" style={typographyFor(site, "homeSessionTitle")}>
            {texts.sessionTitle || "Unlimited Session"}
          </h1>
          <p className="readable-copy text-lg text-gray-300 mb-8" style={typographyFor(site, "homeSessionText")}>
            {texts.sessionDescription ||
              "Unlimited Session, c'est une marque musicale, une mentalite, un mouvement. Un endroit ou la creation n'a aucune limite, ou l'on peut produire sans fin. C'est un reseau underground ou tous les creatifs se retrouvent pour casser les codes de l'industrie musicale."}
          </p>
          <div className="flex gap-4 flex-wrap">
            {showPublicPage("studio") && (
              <Link to={pages.home.sessionButton1Url || "/studio"} className="bg-white text-black px-6 py-2 rounded-full font-semibold hover:bg-gray-300 transition">
                {texts.sessionButton1 || "En savoir plus"}
              </Link>
            )}
            {showPublicPage("booking") && (
              <Link to={pages.home.sessionButton2Url || "/reservation-studio"} className="border border-white px-6 py-2 rounded-full font-semibold hover:bg-white hover:text-black transition">
                {texts.sessionButton2 || "Reserver"}
              </Link>
            )}
          </div>
        </div>
        <div className="mt-12 md:mt-0 md:ml-12 w-40 md:w-64">
          <img src={pages.home.sessionLogo || "/images/logo.png"} alt="Logo Unlimited Session" className="w-full h-auto" />
        </div>
      </section>
      )}

      {/* ===== COVER SLIDER ===== */}
      {visibility.homeCovers && <CoverSlider />}

      {/* ===== MERCH ===== */}
      {visibility.homeMerch && showPublicPage("latdior") && <MerchSection />}

      {/* ===== VIDÉOS ===== */}
      {visibility.homeVideos && showPublicPage("videos") && <VideosSection limit={6} showHeader={true} showSeeAll={true} />}

      {/* ===== TRIPLE CTAs ===== */}
      {visibility.homeCtas && <TripleCtas settings={pages.home.ctas} showContact={showPublicPage("contact")} />}
    </div>
  );
}
